import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Form, Input, Layout, Row, Spin, Typography } from 'antd';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import axios from 'axios';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { EyeInvisibleOutlined, EyeOutlined, LoadingOutlined, LockOutlined, ReloadOutlined, SoundOutlined, UserOutlined } from '@ant-design/icons';
import { BackendAPI } from '../../../axios';
import UserHeader from '../UserHeader';
import { Content, Footer } from 'antd/es/layout/layout';
import Sider from 'antd/es/layout/Sider';
import UserNavbar from '../UserNavbar';
import '../ChangePassword/ChangePassword.scss'
import { useNavigate } from 'react-router-dom';
import type { CSSProperties } from 'react';
const { Title, Text } = Typography;

const MySwal = withReactContent(Swal);

const overlayStyle: CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    // height: '100vh',
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
};

function UserPasswordChange() {
    const navigate = useNavigate();
    const [captchaImage, setCaptchaImage] = useState('');
    const [captchaText, setCaptchaText] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isOnClickSlider, setOnclickSlider] = useState(false);
    const [refreshToken, setRefreshToken] = useState('');
    const [loading, setLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 830);
    const labelStyle = {
        fontWeight: "bold",
        // padding: '0 10px',
        fontSize: '16px',
        // marginBottom: "8px",
    };
const validationSchema = Yup.object().shape({
  cpassword: Yup.string()
    .required('Current password is required'),

  npassword: Yup.string()
    .required('New password is required')
    .min(8, 'Password must be at least 8 characters long')
    .notOneOf(
      [Yup.ref('cpassword')],
      'New password must be different from current password'
    )
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d).{8,}$/,
      'Password must contain both letters and numbers'
    ),

  ncpassword: Yup.string()
    .required('Confirm password is required')
    .oneOf(
      [Yup.ref('npassword')],
      'Confirm password must match new password'
    ),

  captcha: Yup.string()
    .required('Captcha is required')
    .test(
      'captcha-matches',
      'Incorrect captcha',
      (value) => value === captchaText
    ),
});
    const { control, watch, handleSubmit, formState: { errors, touchedFields, isSubmitted } } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onTouched',
    });
    const handleLogout = async () => {
        try {
            const response = await BackendAPI.post("/user/logout", {}, { withCredentials: true });

            if (response.status === 200) {
                localStorage.removeItem("authToken");
                localStorage.removeItem("initialTime");
                localStorage.removeItem("lastSavedTime");
                localStorage.removeItem("remainingTime");
                navigate("/");
            }
        } catch (error) {
        }
    };
    const onSubmit = async (data: any) => {
        setLoading(true);
        console.log("Form submitted:", data);

        const url = `/user/updatePassword`;
        const header = {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${refreshToken}`,
            },

        };

         const body = {
        password: data.npassword,
        oldpassword: data.cpassword
    };

        try {
            const response = await BackendAPI.post(url,body, header);
            console.log('Response from server:', response.data);
            if (response.data === 'successtosendmail') {
                MySwal.fire({
                    icon: 'success',
                    imageWidth: 100,
                    imageHeight: 100,
                    title: 'Password Updated!',
                    text: 'Your password has been changed successfully. Please check your email for confirmation.',
                    confirmButtonText: 'OK',
                }).then((result) => {
                    if (result.isConfirmed) {
                        handleLogout();
                    }
                });
            } else if (response.data === 'The current password entered is incorrect') {
                MySwal.fire({
                    icon: 'error',
                    imageWidth: 100,
                    imageHeight: 100,
                    title: 'Incorrect Password!',
                    text: 'The current password you entered is incorrect. Please try again.',
                });
            } else if (response.data === 'Old Password and New Password is same') {
                MySwal.fire({
                    icon: 'warning',
                    imageWidth: 100,
                    imageHeight: 100,
                    title: 'Password Not Changed',
                    text: 'Your new password cannot be the same as your old one. Please choose a different password.',
                });
            } else if (response.data === 'failtosendmail') {
                MySwal.fire({
                    icon: 'warning',
                    title: 'Password Updated',
                    text: 'Your password was updated successfully, but we were unable to send the confirmation email. Please check your email settings or contact support if the issue persists.',
                    imageWidth: 100,
                    imageHeight: 100,
                    confirmButtonText: 'OK',
                }).then((result) => {
                    if (result.isConfirmed) {
                        handleLogout();
                    }
                });
            }
            setLoading(false);
        } catch (error) {
            console.error('Error:', error);
            setLoading(false);
            MySwal.fire({
                    icon: 'warning',
                    imageWidth: 100,
                    imageHeight: 100,
                    title: 'Password Not Changed',
                    text: 'Something Went Wrong',
                });
        }
    };

    const getRefreshToken = async () => {
        try {
            const response = await BackendAPI.get("/user/refresh-token", { withCredentials: true });
            setRefreshToken(response.data);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                if (error.request.status == 401) {
                    window.location.reload();
                }
            }
        }
    };
    useEffect(() => {
        const executeFunctions = async () => {
            await fetchCaptchaImage();
            await getRefreshToken();
        };
        executeFunctions();
    }, []);

    const fetchCaptchaImage = async () => {
        const header = {
            headers: {
                "Content-Type": "application/json",
            },
        };
        const url = `/user/generate`;
        try {
            const response = await BackendAPI.get(url, header);
            //   const result = response.data;
            setCaptchaImage(response.data.base64Image);
            setCaptchaText(response.data.captchaText);
        } catch (error) {
            console.error('Error fetching total count:', error);
        }
    };
const playCaptchaAudio = () => {
  if (!captchaText) return;
console.warn('Captcha not loaded');
  // Stop any ongoing speech
  window.speechSynthesis.cancel();

  const spokenText = captchaText
    .split('')
    .map(char => {
      if (/[a-z]/.test(char)) return `lowercase ${char}`;
      if (/[A-Z]/.test(char)) return `uppercase ${char}`;
      if (/[0-9]/.test(char)) return `digit ${char}`;
      return char;
    })
    .join(', ');

  const utterance = new SpeechSynthesisUtterance(spokenText);

  utterance.lang = 'en-US';
  utterance.rate = 0.6;   // very slow
  utterance.pitch = 1;
  utterance.volume = 1;

  window.speechSynthesis.speak(utterance);
};
useEffect(() => {
  const handleResize = () => {
    setIsMobile(window.innerWidth < 830);
  };

  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, []);
    return (
        <>
            {loading && (
                <Spin spinning={true} style={{ position: 'absolute', top: '50%', left: '50%', zIndex: 9999 }} indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
            )}
            {/* <div>
                <UserHeader setIsOnClickSlider={setOnclickSlider} />
            </div> */}
            <Layout style={{ minHeight: '89vh', }}>

                <Sider
                    collapsed={isMobile ? true : isOnClickSlider}
                    // collapsed={isOnClickSlider}
                    // trigger={null} // disable default trigger
                    // collapsible
                    collapsedWidth={50}
                    // onCollapse={handleCollapse}
                    style={{
                        // height: '90vh',
                        background: 'white',
                        position: 'relative',
                        overflow: 'auto',
                        top: '0px',
                        paddingTop: '20px',
                    }}
                >
                    {/* Custom collapse icon at top */}

                    <div style={{ top: '0', position: 'sticky', height: '100%', }}>
                        {/* Add menu or other content here */}
                        {/* <div
                            style={{
                                position: 'absolute',
                                top: 0, textAlignLast: 'right',
                                cursor: 'pointer', width: '100%', padding: '6px 16px',
                            }}
                            onClick={handleCollapse}
                        >
                            {collapsed ? <MenuUnfoldOutlined style={{ fontSize: '30px', }} /> : <MenuFoldOutlined style={{ fontSize: '30px', }} />}
                        </div> */}
                        <UserNavbar />
                        {/* <div style={{ textAlignLast: 'center', transition: 'all 0.9s', display: isOnClickSlider ? 'none' : 'block', position: 'absolute', justifySelf: 'center', bottom: '2%', }}>
                            <img src={cdac_logo} alt="Status" style={{ width: '40%', height: 'auto' }} />
                            <h5 style={{ textAlign: 'center', margin: '0' }}>Center for Development of Advanced Computing (C-DAC)</h5>
                        </div> */}
                    </div>
                </Sider>
                <Layout>
                    <Content style={{ margin: '16px 16px 0', overflowY: 'auto', maxHeight: 'calc(100vh - 80px)', paddingBottom: 32 }} className='user-changepassword-section'>
                        <Row justify="center" align="middle" style={{ background: "#f5f6fa" }}>
                            <Col xs={22} sm={20} md={16} lg={12} xl={10}>
                                <Card
                                    bordered={false}
                                    style={{
                                        borderRadius: 12,
                                        boxShadow: "0 8px 24px rgba(0,0,0,0.08)"
                                    }}
                                >
                                    {/* <Title level={3} style={{ textAlign: "center", color: "#1515b4" }}>
                        Change Password
                    </Title> */}
                                    <div className="card-header">
                                        <LockOutlined className="header-icon" />
                                        <Title level={2} className="card-title">Change Password</Title>
                                        <Text className="card-subtitle">
                                            Please enter your current password and choose a new password
                                        </Text>
                                    </div>

                                    <form onSubmit={handleSubmit(onSubmit)}>

                                        {/* Current Password */}
                                        <Controller
                                            name="cpassword"
                                            control={control}
                                            render={({ field }) => (
                                                <div style={{ marginBottom: 16 }}>
                                                    <span style={{ fontSize: '16px', fontWeight: 'bold' }}>Current Password</span>
                                                    <Input.Password
                                                        {...field}
                                                        placeholder="Enter Current Password"
                                                        autoComplete="new-password"
                                                        size="large"
                                                        style={{ marginTop: '6px' }}
                                                    />
                                                    <Text type="danger">{errors.cpassword?.message}</Text>
                                                </div>
                                            )}
                                        />

                                        {/* New Password */}
                                        <Controller
                                            name="npassword"
                                            control={control}
                                            render={({ field }) => (
                                                <div style={{ marginBottom: 16 }}>
                                                    <span style={{ fontSize: '16px', fontWeight: 'bold' }}>New Password</span>
                                                    <Input.Password
                                                        {...field}
                                                        placeholder="Enter New Password"
                                                        size="large"
                                                        autoComplete="new-password"
                                                        style={{ marginTop: '6px' }}
                                                    />
                                                    <Text type="danger">{errors.npassword?.message}</Text>
                                                </div>
                                            )}
                                        />

                                        {/* Confirm Password */}

                                        <Controller
                                            name="ncpassword"
                                            control={control}
                                            
                                            render={({ field }) => (
                                                <div style={{ marginBottom: 16 }}>
                                                    <span style={{ fontSize: '16px', fontWeight: 'bold' }}>Confirm Password</span>
                                                    <Input.Password
                                                        {...field}
                                                        placeholder="Enter Confirm Password"
                                                        size="large"
                                                        autoComplete="new-password"
                                                        style={{ marginTop: '6px' }}
                                                    />
                                                    <Text type="danger">{errors.ncpassword?.message}</Text>
                                                </div>
                                            )}
                                        />

                                        {/* Captcha */}
                                        <div style={{ marginBottom: 8, display: 'flex', gap: '6px', }}>
                                            <div
                                                style={{
                                                    color: "#fff",
                                                    width: '100%',
                                                    borderRadius: '10px',
                                                    border: '2ps solid blue',
                                                    // padding: "8px 16px",
                                                    // borderRadius: 6,
                                                    // fontWeight: 600,
                                                    // letterSpacing: 3,
                                                    // display: "inline-block",
                                                    // marginBottom: 8
                                                }}
                                            >
                                                <img src={`data:image/png;base64,${captchaImage}`} alt="CAPTCHA" style={{ width: '100%', height: '50px', borderRadius: '10px', border: '2px solid #1515b4' }} />
                                            </div>

                                            <Button
                                                type="link"
                                                onClick={fetchCaptchaImage}
                                                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontSize: '24px,', height: '50px', color: 'white', width: '12%', }}
                                            >
                                                <ReloadOutlined />
                                            </Button>
                                            <Button
                                                type="link"
                                                onClick={playCaptchaAudio}
                                                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontSize: '24px,', height: '50px', color: 'white', width: '12%', }}
                                            >
                                                <SoundOutlined />
                                            </Button>
                                        </div>

                                        <Controller
                                            name="captcha"
                                            control={control}
                                            render={({ field }) => (
                                                <div style={{ marginBottom: 16 }}>
                                                    <span style={{ fontSize: '16px', fontWeight: 'bold'}}>Captcha</span>
                                                    <Input
                                                        {...field}
                                                        placeholder="Enter Captcha"
                                                        size="large"
                                                        style={{marginTop: '6px'}}
                                                    />
                                                    <Text type="danger">{errors.captcha?.message}</Text>
                                                </div>
                                            )}
                                        />

                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            loading={loading}
                                            block
                                            size="large"
                                            style={{
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                borderColor: "#1515b4",
                                                borderRadius: 6
                                            }}
                                        >
                                            Update Password
                                        </Button>

                                    </form>
                                    {/* Password Requirements */}
                                    <div className="password-requirements">
                                        <Text type="secondary" style={{ fontSize: '13px' }}>
                                            <strong>Password Requirements:</strong>
                                            <ul>
                                                <li>At least 8 characters long</li>
                                                <li>Must be different from current password</li>
                                                <li>Should contain letters and numbers for better security</li>
                                            </ul>
                                        </Text>
                                    </div>
                                </Card>
                            </Col>
                        </Row>
                    </Content>
                    <Footer style={{ textAlign: 'center' }}>
                        ©2025 Copyright: C-DAC Mumbai. All Rights Reserved
                    </Footer>
                </Layout>
            </Layout>
        </>
    )
};

export default UserPasswordChange
